import { Component, inject, signal } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidationErrors, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RecaptchaModule } from 'ng-recaptcha';
import { CommonModule } from '@angular/common';
import { environment } from 'src/environments/environment';
import { RecaptchaService } from '../../core/services/recaptcha.service';
import { TicketService } from '../../core/services/ticket.service';
import { catchError, tap } from 'rxjs/operators';
import { SharedModule } from '../../shared/shared.module';
import { firstValueFrom } from 'rxjs';
import { CheckoutService } from '../checkout/checkout.service';
import { AuthService } from '../../core/services/auth.service';

type DocType = '1' | '2' | '3' | '4' | '5';

@Component({
  selector: 'app-refund-tickets',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    RecaptchaModule,
    SharedModule,
  ],
  templateUrl: './refund-tickets.component.html'
})
export class RefundTicketsComponent {
  private fb = inject(FormBuilder);
  private recaptchaService = inject(RecaptchaService);
  private ticketService = inject(TicketService);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private checkoutService = inject(CheckoutService);
  
  recaptchaSiteKey = environment.recaptchaSiteKey;
  loading = signal(false);
  recaptchaError = signal<string>('');
  ticket = signal<any>(null);
  busqueda = signal<boolean>(false);
  refunding = signal<boolean>(false);

  docTypes: { value: DocType; label: string }[] = [
    { value: '1', label: 'D.N.I.' },
    { value: '2', label: 'C.U.I.T.' },
    { value: '3', label: 'Pasaporte' },
    { value: '4', label: 'Libreta Cívica' },
    { value: '5', label: 'Cédula de Identidad' },
  ];

  form = this.fb.group(
    {
      docType: this.fb.control<DocType | null>(null, { validators: [Validators.required] }),
      docNumber: this.fb.control<string>('', { validators: [Validators.required, Validators.pattern(/^\d{6,12}$/)] }),
      travelDate: this.fb.control<Date | null>(null, { validators: [Validators.required] }),
      email: this.fb.control<string>('', { validators: [Validators.email] }),
      ticketNumber: this.fb.control<string>('', { validators: [] }),
      refundCode: this.fb.control<string>('', { validators: [] }),
    },
    { validators: [atLeastOneOf(['email', 'ticketNumber', 'refundCode'])] }
  );

  // Variable separada para el token del reCAPTCHA
  recaptchaToken = signal<string | null>(null);

  // UI helpers
  isInvalid(controlName: string): boolean {
    const c = this.form.get(controlName);
    return !!c && c.invalid && (c.dirty || c.touched);
  }

  onRecaptchaResolved(token: string | null): void {
    this.recaptchaToken.set(token);
    this.recaptchaError.set('');
  }

  onRecaptchaError(): void {
    this.recaptchaError.set('Error al completar el reCAPTCHA. Intentá nuevamente.');
    this.recaptchaToken.set(null);
  }

  async submit() {
    if (this.form.invalid || !this.recaptchaToken()) {
      this.form.markAllAsTouched();
      if (!this.recaptchaToken()) {
        this.recaptchaError.set('Completá el reCAPTCHA para continuar.');
      }
      return;
    }

    this.loading.set(true);
    this.recaptchaError.set('');

    const formData = this.form.getRawValue();
    const recaptchaToken = this.recaptchaToken();

    if (!recaptchaToken) {
      this.recaptchaError.set('Completá el reCAPTCHA para continuar.');
      this.loading.set(false);
      return;
    }

    try {
      // Verificar el reCAPTCHA primero
      const verificationResult = await this.recaptchaService.verifyToken(recaptchaToken, 'refund_tickets')
        .pipe(
          tap(response => {
            if (!this.recaptchaService.isValid(response)) {
              const errors = this.recaptchaService.getErrorMessages(response);
              throw new Error(errors.join(', ') || 'Verificación del reCAPTCHA falló');
            }
          }),
          catchError(error => {
            console.error('Error verificando reCAPTCHA:', error);
            throw new Error(error.message || 'Error al verificar el reCAPTCHA');
          })
        ).toPromise();

      // Si la verificación es exitosa, proceder con el envío
      const payload = {
        departure_datetime: formData.travelDate?.toISOString().split('T')[0] || '', // Formato YYYY-MM-DD
        document_type_id: formData.docType!,
        document_number: formData.docNumber!,
        email: formData.email!,
        ticket_number: formData.ticketNumber!,
        refund_code: formData.refundCode!,
      };
      this.loading.set(true);
      this.busqueda.set(true);
      console.log('Buscar pasaje ->', payload);
      // Llamar al servicio HTTP para reenviar los pasajes
      this.ticketService.searchTickets(payload).subscribe({
        next: (response: any) => {
          this.ticket.set(response);
        },
        error: (error: any) => {
          console.error('Error al reenviar tickets:', error);
          const errorMessage = error.error?.message || 'Error al devolver los tickets';
          this.snackBar.open(errorMessage, 'Cerrar', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
        }
      });      
    } catch (error: unknown) {
      console.error('Error en submit:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al procesar la solicitud';
      this.recaptchaError.set(errorMessage);
      this.snackBar.open(errorMessage, 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
    } finally {
      this.loading.set(false);
    }
  }

  async refundTicket(ticket: any) {
    this.refunding.set(true);
    console.log('Devolver pasaje ->', ticket);
    const operacion = this.mapOperacion(ticket);
    const operacionRes: any = await firstValueFrom(this.checkoutService.crearOperacion(operacion));
    const operationId = operacionRes.data.id;
    console.log('Operacion creada ->', operationId);
    //const ticketRes: any = await firstValueFrom(this.checkoutService.asociarTicket(operationId, ticket.data.id));
    
  }

  mapOperacion(ticket: any) {
    return {
      user_id: this.authService.getCurrentUser()?.id || null,
      total_amount: ticket.data.ticket_price,
      subtotal_amount: ticket.data.ticket_price,
      operation_type_id: 2, //2: devolucion
      operation_status_id: 3, // 3: pendiente
      discount_amount: 0,
      service_cost: ticket.data.service_cost,
      provider_id: ticket.provider_id
    }
  }
}





/** Validador de formulario: al menos uno lleno entre una lista de campos */
function atLeastOneOf(fieldNames: string[]) {
  return (group: AbstractControl): ValidationErrors | null => {
    const hasOne = fieldNames.some(name => {
      const v = group.get(name)?.value;
      return v !== null && v !== undefined && String(v).trim() !== '';
    });
    return hasOne ? null : { atLeastOne: true };
  };
}
